/* =========================================================
   Corals Anonymous - Cart Drawer State Sync

   Purpose:
   - Keeps drawer/footer/header cart badge aligned with /cart.js.
   - Treats shipping box, shipping protection, and insurance as add-ons.
   - Blocks checkout when cart has no real customer products.
   - Hides the stable footer wrapper when cart has no real customer products.
   - Corrects the header cart badge so add-ons do not count as items.
   - Does NOT replace/re-render the footer, so SimplyInsurance does not remount/stutter.
   ========================================================= */

(function () {
  const DRAWER_SELECTOR = '#MinimogCartDrawer';
  const FOOTER_SELECTOR = '#MinimogCartDrawerFooter';

  const CHECKOUT_SELECTOR = [
    '[data-ca-checkout-cta]',
    '.ca-checkout-cta',
    'button[name="checkout"]',
    'a[href="/checkout"]',
    'a[href*="/checkout"]',
    '[data-ca-bird-continue]'
  ].join(',');

  const CART_BADGE_SELECTOR = [
    '[data-cart-count]',
    '.m-cart-count',
    '.cart-count-bubble',
    '.m-cart-icon-bubble__count',
    '.m-cart-count-bubble'
  ].join(',');

  const CART_ACTION_SELECTOR = [
    '#MinimogCartDrawer a[href*="/cart/change"]',
    '#MinimogCartDrawer a[href*="quantity=0"]',
    '#MinimogCartDrawer .m-cart-item__remove',
    '#MinimogCartDrawer .m-cart-drawer__remove',
    '#MinimogCartDrawer [data-cart-remove]',
    '#MinimogCartDrawer m-cart-remove-button',
    '#MinimogCartDrawer button[name="minus"]',
    '#MinimogCartDrawer button[name="plus"]',
    '#MinimogCartDrawer [data-qty-button]',
    '#MinimogCartDrawer .m-quantity__button'
  ].join(',');

  const CART_INPUT_SELECTOR = [
    '#MinimogCartDrawer input[name="updates[]"]',
    '#MinimogCartDrawer input[type="number"]',
    '#MinimogCartDrawer .m-quantity__input'
  ].join(',');

  let debounceTimer = null;
  let isSyncing = false;
  let needsFollowupSync = false;

  function isShippingAddon(item) {
    const title = String(item.product_title || item.title || '').toLowerCase();
    const handle = String(item.handle || '').toLowerCase();
    const vendor = String(item.vendor || '').toLowerCase();
    const sku = String(item.sku || '').toLowerCase();

    return (
      title.includes('shipping protection') ||
      title.includes('shipping box') ||
      title.includes('box charge') ||
      title.includes('insurance') ||
      handle.includes('shipping-protection') ||
      handle.includes('shipping-box') ||
      handle.includes('box-charge') ||
      vendor.includes('simplyinsurance') ||
      vendor.includes('simply insurance') ||
      sku.includes('shipping-protection') ||
      sku.includes('box')
    );
  }

  function getDrawer() {
    return document.querySelector(DRAWER_SELECTOR);
  }

  function getFooter() {
    return document.querySelector(FOOTER_SELECTOR);
  }

  function getCheckoutButtons() {
    const drawer = getDrawer();

    if (!drawer) return [];

    return Array.from(drawer.querySelectorAll(CHECKOUT_SELECTOR));
  }

  function drawerLooksOpen() {
    const drawer = getDrawer();

    if (!drawer) return false;

    return (
      drawer.classList.contains('m-cart-drawer--active') ||
      drawer.classList.contains('active') ||
      drawer.classList.contains('open') ||
      drawer.hasAttribute('open') ||
      drawer.getAttribute('aria-hidden') === 'false'
    );
  }

  function forceBackdropClosedIfDrawerClosed() {
    if (drawerLooksOpen()) return;

    document.body.classList.remove('ca-cart-drawer-open');
    document.documentElement.classList.remove('ca-cart-drawer-open');
    document.body.classList.remove('m-cart-drawer-open');
    document.documentElement.classList.remove('m-cart-drawer-open');

    const backdrop = document.querySelector('[data-ca-cart-drawer-backdrop]');

    if (backdrop) {
      backdrop.style.pointerEvents = '';
      backdrop.style.opacity = '';
      backdrop.style.visibility = '';
    }
  }

  function fetchCart() {
    return fetch('/cart.js', {
      method: 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    }).then(function (response) {
      if (!response.ok) throw new Error('Could not fetch cart.js');
      return response.json();
    });
  }

  function countCartItems(cart) {
    let customerItemCount = 0;
    let shippingAddonCount = 0;

    if (!cart || !Array.isArray(cart.items)) {
      return {
        customerItemCount: 0,
        shippingAddonCount: 0
      };
    }

    cart.items.forEach(function (item) {
      const quantity = Number(item.quantity || 0);

      if (isShippingAddon(item)) {
        shippingAddonCount += quantity;
      } else {
        customerItemCount += quantity;
      }
    });

    return {
      customerItemCount: customerItemCount,
      shippingAddonCount: shippingAddonCount
    };
  }

  function findBadgeInsideCartWrapper(wrapper) {
    if (!wrapper) return null;

    return (
      wrapper.querySelector(CART_BADGE_SELECTOR) ||
      wrapper.querySelector('sup') ||
      wrapper.querySelector('[aria-label*="cart item"]') ||
      wrapper.querySelector('[aria-label*="Cart item"]')
    );
  }

  function setBadgeValue(badge, customerItemCount) {
    if (!badge) return;

    const countText = String(customerItemCount);

    badge.textContent = countText;
    badge.setAttribute('data-count', countText);
    badge.setAttribute('aria-label', countText + ' cart items');

    if (customerItemCount > 0) {
      badge.hidden = false;
      badge.classList.remove('m:hidden');
      badge.classList.remove('hidden');
      badge.style.display = '';
      badge.style.visibility = '';
      badge.style.opacity = '';
    } else {
      badge.hidden = true;
      badge.classList.add('m:hidden');
      badge.classList.add('hidden');
      badge.style.display = 'none';
      badge.style.visibility = 'hidden';
      badge.style.opacity = '0';
    }
  }

  function updateCartBubbleCount(customerItemCount) {
    /*
      Correct the header cart badge to count only real customer products.
      Do not set textContent on .m-cart-icon-bubble itself because that wrapper
      contains the actual cart icon/link.
    */
    document.querySelectorAll('.m-cart-icon-bubble').forEach(function (wrapper) {
      const badge = findBadgeInsideCartWrapper(wrapper);

      wrapper.dataset.caRealCartCount = String(customerItemCount);
      wrapper.classList.toggle('ca-cart-icon-has-real-items', customerItemCount > 0);
      wrapper.classList.toggle('ca-cart-icon-empty-or-addons-only', customerItemCount <= 0);

      if (!badge || badge === wrapper) return;

      setBadgeValue(badge, customerItemCount);
    });

    /*
      Also update standalone count elements used by Minimog.
    */
    document.querySelectorAll(CART_BADGE_SELECTOR).forEach(function (badge) {
      if (badge.classList && badge.classList.contains('m-cart-icon-bubble')) return;

      setBadgeValue(badge, customerItemCount);
    });
  }

  function setCheckoutState(hasCustomerItems, customerItemCount) {
    getCheckoutButtons().forEach(function (button) {
      button.dataset.caCustomerItemCount = String(customerItemCount);

      if (hasCustomerItems) {
        button.removeAttribute('aria-disabled');
        button.removeAttribute('disabled');
        button.classList.remove('disabled');
        button.classList.remove('m-button--disabled');
        button.classList.remove('ca-checkout-disabled');

        if ('disabled' in button) {
          button.disabled = false;
        }

        button.style.pointerEvents = '';
      } else {
        button.setAttribute('aria-disabled', 'true');
        button.setAttribute('disabled', 'disabled');
        button.classList.add('disabled');
        button.classList.add('ca-checkout-disabled');

        if ('disabled' in button) {
          button.disabled = true;
        }

        button.style.pointerEvents = 'none';
      }
    });
  }

  function setFooterState(hasCustomerItems, customerItemCount, shippingAddonCount) {
    const footer = getFooter();

    if (!footer) return;

    footer.dataset.caCustomerItemCount = String(customerItemCount);
    footer.dataset.caShippingAddonCount = String(shippingAddonCount);
    footer.dataset.caHasCustomerItems = hasCustomerItems ? 'true' : 'false';

    footer.classList.toggle('ca-cart-footer--empty', !hasCustomerItems);

    /*
      Keep the footer DOM stable so SimplyInsurance does not remount/stutter,
      but hide the entire footer wrapper when there are no real customer products.
    */
    if (hasCustomerItems) {
      footer.hidden = false;
      footer.style.display = '';
      footer.style.visibility = '';
      footer.style.opacity = '';
      footer.style.pointerEvents = '';
    } else {
      footer.hidden = true;
      footer.style.display = 'none';
      footer.style.visibility = 'hidden';
      footer.style.opacity = '0';
      footer.style.pointerEvents = 'none';
    }
  }

  function applyCartState(cart) {
    const drawer = getDrawer();

    if (!drawer) {
      const countsWithoutDrawer = countCartItems(cart);
      updateCartBubbleCount(countsWithoutDrawer.customerItemCount);
      return;
    }

    const counts = countCartItems(cart);
    const customerItemCount = counts.customerItemCount;
    const shippingAddonCount = counts.shippingAddonCount;

    const hasCustomerItems = customerItemCount > 0;
    const hasOnlyAddons = !hasCustomerItems && shippingAddonCount > 0;

    updateCartBubbleCount(customerItemCount);

    drawer.dataset.caCustomerItemCount = String(customerItemCount);
    drawer.dataset.caShippingAddonCount = String(shippingAddonCount);

    drawer.classList.toggle('m-cart--empty', !hasCustomerItems);
    drawer.classList.toggle('ca-cart-has-real-items', hasCustomerItems);
    drawer.classList.toggle('ca-cart-has-only-addons', hasOnlyAddons);

    document.body.classList.toggle('ca-cart-has-real-items', hasCustomerItems);
    document.body.classList.toggle('ca-cart-has-only-addons', hasOnlyAddons);

    setFooterState(hasCustomerItems, customerItemCount, shippingAddonCount);
    setCheckoutState(hasCustomerItems, customerItemCount);
  }

  function runSync() {
    if (isSyncing) {
      needsFollowupSync = true;
      return;
    }

    isSyncing = true;

    fetchCart()
      .then(applyCartState)
      .catch(function (error) {
        console.warn('[CA Cart Drawer State Sync] Failed:', error);
      })
      .finally(function () {
        isSyncing = false;
        forceBackdropClosedIfDrawerClosed();

        if (needsFollowupSync) {
          needsFollowupSync = false;
          queueCartStateSync(150);
        }
      });
  }

  /*
    Debounced sync:
    Good for normal events where repeated calls should collapse into one check.
  */
  function syncCartState(delay) {
    window.clearTimeout(debounceTimer);

    debounceTimer = window.setTimeout(function () {
      runSync();
    }, typeof delay === 'number' ? delay : 120);
  }

  /*
    Queued sync:
    Good for remove/quantity actions, where we intentionally want multiple
    follow-up checks after Minimog has finished updating /cart.js.
  */
  function queueCartStateSync(delay) {
    window.setTimeout(function () {
      runSync();
    }, typeof delay === 'number' ? delay : 120);
  }

  function syncAfterCartMutation() {
    queueCartStateSync(150);
    queueCartStateSync(450);
    queueCartStateSync(900);
    queueCartStateSync(1400);
  }

  /*
    Expose globally so cart-drawer.js, atc-variants.js, insurance-bridge.js,
    and duplicate-merge logic can request a state check after rendering.
  */
  window.CA_SYNC_CART_DRAWER_STATE = syncCartState;
  window.CA_SYNC_EMPTY_CART_FOOTER = syncCartState;
  window.CA_UPDATE_REAL_CART_BADGE = function () {
    queueCartStateSync(0);
  };

  /*
    Block checkout when there are no real customer items.
  */
  document.addEventListener(
    'click',
    function (event) {
      const checkoutButton = event.target && event.target.closest
        ? event.target.closest(CHECKOUT_SELECTOR)
        : null;

      if (!checkoutButton) return;

      const count = Number(checkoutButton.dataset.caCustomerItemCount || 0);
      const disabled = checkoutButton.getAttribute('aria-disabled') === 'true';

      if (count > 0 && !disabled) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      queueCartStateSync(0);
      queueCartStateSync(300);
    },
    true
  );

  /*
    Sync after initial load.
  */
  document.addEventListener('DOMContentLoaded', function () {
    queueCartStateSync(0);
    queueCartStateSync(700);
  });

  /*
    Sync after known cart events.
  */
  document.addEventListener('cart:updated', function () {
    syncAfterCartMutation();
  });

  document.addEventListener('cart:refresh', function () {
    syncAfterCartMutation();
  });

  document.addEventListener('cart:change', function () {
    syncAfterCartMutation();
  });

  document.addEventListener('minimog:cart:refresh', function () {
    syncAfterCartMutation();
  });

  document.addEventListener('ca:cart-drawer:rendered', function () {
    queueCartStateSync(0);
    queueCartStateSync(350);
  });

  document.addEventListener('ssp:cart:update', function () {
    queueCartStateSync(300);
    queueCartStateSync(900);
    queueCartStateSync(1500);
  });

  document.addEventListener('shopify:section:load', function () {
    queueCartStateSync(80);
    queueCartStateSync(650);
  });

  /*
    Watch normal cart forms without trying to manage drawer DOM directly.
  */
  document.addEventListener(
    'submit',
    function (event) {
      const form = event.target;

      if (!form || !form.action) return;

      const action = String(form.action);

      if (
        action.includes('/cart/add') ||
        action.includes('/cart/change') ||
        action.includes('/cart/update')
      ) {
        syncAfterCartMutation();
      }
    },
    true
  );

  /*
    Critical:
    Minimog drawer remove/quantity buttons may update /cart.js without emitting
    cart events consistently. This catches removing the final real item, so the
    footer hides when only the box charge/protection add-ons remain.
  */
  document.addEventListener(
    'click',
    function (event) {
      const cartAction = event.target && event.target.closest
        ? event.target.closest(CART_ACTION_SELECTOR)
        : null;

      if (!cartAction) return;

      syncAfterCartMutation();
    },
    true
  );

  document.addEventListener(
    'change',
    function (event) {
      const cartInput = event.target && event.target.closest
        ? event.target.closest(CART_INPUT_SELECTOR)
        : null;

      if (!cartInput) return;

      syncAfterCartMutation();
    },
    true
  );

  /*
    Backdrop/tint cleanup only. This does not re-render the drawer.
  */
  document.addEventListener('click', function () {
    window.setTimeout(forceBackdropClosedIfDrawerClosed, 150);
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') {
      window.setTimeout(forceBackdropClosedIfDrawerClosed, 150);
    }
  });
})();